import { routeParts, type AnyViewName, type CommandName, type CommandBody, type CommandAnswer, type Route } from "@nabla/api";
import type { ViewAnswer } from "@nabla/api";
import { readAnswer, type Schema } from "./answers.ts";
import { getJson, sendJson, type ClientError, type Reach, type Reply } from "./http.ts";

const host: Reach = { fetch: (url, init) => fetch(url, init), api: NABLA_API, seen: () => undefined };
type Read<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: ClientError };

export function publicRead<K extends AnyViewName>(name: K, route: Route<"GET">): Promise<Read<ViewAnswer<K>>>;
export async function publicRead(name: AnyViewName, route: Route<"GET">): Promise<Read<unknown>> {
  const answer = await getJson(host, routeParts(route).path);
  if (!answer.ok) return answer;
  const read = readAnswer<unknown>(answer.held.body, answer.held.sameApi, (api) => api.views[name].answer);
  return read.ok ? read : { ok: false, error: { kind: "formato", detail: read.detail } };
}

export function publicSend<K extends CommandName>(name: K, route: Route, body: CommandBody<K>): Promise<Reply<CommandAnswer<K> | null>>;
export function publicSend(name: CommandName, route: Route, body: object): Promise<Reply<unknown>> {
  return sendJson<unknown>(host, route, {}, body, (api) => api.commands[name].answer ?? nothing);
}
const nothing: Schema<null> = { safeParse: () => ({ success: true, data: null }) };

export function tabProof(key: string, create = false): string | undefined {
  try {
    if (!create) return sessionStorage.getItem(key) ?? undefined;
    const value = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
      .replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    sessionStorage.setItem(key, value);
    return value;
  } catch { return undefined; }
}

export function keepInTab(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { return; }
}

export function forgetTabProof(key: string): void {
  try { sessionStorage.removeItem(key); } catch { return; }
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}
